import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    completeMovmoOAuth,
    MissingVerifierError,
    StateMismatchError,
} from "@movmo/connect-button";
import {
    linkCompleteWithSession,
    linkLoyaltyWithSession,
} from "../../services/demoAirlineApi";
import { buildRedirectUri } from "../../config/movmoIdentity";
import {
    buildLinkRecordFromRedirect,
    writeToastFlag,
} from "../../services/mockLoyalty";
import type { MovmoDeclinedState } from "../../components/MovmoIdentityChip";

type Status = "pending" | "error";

/**
 * OAuth callback for Philippine Airlines' one-click Movmo identity chip.
 * Registered at the EXACT path pa-demo-airline's
 * OAuth client has provisioned as its redirect_uri — see
 * src/config/movmoIdentity.ts. Mirrors AeroConnectCallback.tsx.
 *
 * MOVMO-541 loyalty-in-consent: the Movmo consent screen (auth-ui) captures
 * the traveler's Mabuhay Miles decision and hands the outcome back here as
 * extra query params on the OAuth redirect (loyalty_status / loyalty_member_id
 * / loyalty_import / loyalty_program — demo-only transport; the real
 * architecture carries this as signed claims or an S2S callback, MOVMO-542).
 * This callback writes the airline-side link record and queues the
 * confirmation toast the landing page shows after navigation.
 *
 * Once the link completes the PA session carries the traveler's Movmo
 * identity, so the landing page greets them from GET /me (Flow B): this page
 * navigates straight there. Declining on the Movmo consent screen
 * (?error=access_denied) returns to the landing page with a neutral notice.
 */
export default function PaConnectCallback() {
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>("pending");
    const [message, setMessage] = useState(
        "Connecting your Movmo account…",
    );
    const didRun = useRef(false);

    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        const errorParam = params.get("error");

        if (errorParam) {
            navigate("/philippine-airlines", {
                replace: true,
                state:
                    errorParam === "access_denied"
                        ? ({ movmoDeclined: true } as MovmoDeclinedState)
                        : undefined,
            });
            return;
        }
        if (!code || !state) {
            setStatus("error");
            setMessage("Missing code or state in the callback URL.");
            return;
        }

        const loyaltyRecord = buildLinkRecordFromRedirect(params);
        if (loyaltyRecord) {
            setMessage("Linking your Mabuhay Miles…");
        }

        completeMovmoOAuth(code, state)
            .then((completion) =>
                linkCompleteWithSession("pa", {
                    code: completion.code,
                    code_verifier: completion.codeVerifier,
                    redirect_uri: buildRedirectUri("pa"),
                }),
            )
            .then(async () => {
                if (loyaltyRecord) {
                    // Queues the one-shot confirmation toast the landing
                    // page shows once — copy only, not the resting link
                    // state (see the real link call below).
                    writeToastFlag(loyaltyRecord);
                    // MOVMO-542: complete the loyalty-in-consent decision
                    // against the REAL backend BEFORE navigating, so the
                    // landing's GET /me sees the just-linked state instead
                    // of racing it. record.path
                    // "enrolled" came from `loyalty_status=joined` on the
                    // redirect (buildLinkRecordFromRedirect); anything else
                    // parsed here is the "linked" decision.
                    try {
                        await linkLoyaltyWithSession(
                            "pa",
                            loyaltyRecord.path === "enrolled"
                                ? { enroll: true }
                                : { from_consent: true },
                        );
                    } catch (err) {
                        // Never strand the callback over this — the
                        // traveler still lands linked (just without
                        // loyalty yet); they can redo it via the late-link
                        // modal, which surfaces real errors inline.
                        console.warn(
                            "[pa] loyalty-in-consent link call failed; landing will show unlinked:",
                            err,
                        );
                    }
                }
                navigate("/philippine-airlines", { replace: true });
            })
            .catch((err) => {
                if (
                    err instanceof StateMismatchError ||
                    err instanceof MissingVerifierError
                ) {
                    console.error(
                        "[pa] Movmo connect callback rejected:",
                        err,
                    );
                    navigate("/philippine-airlines", { replace: true });
                    return;
                }
                console.error("[pa] Movmo connect-link failed:", err);
                if (loyaltyRecord) {
                    // The consent-screen loyalty decision is deliberately NOT
                    // written on failure (an airline-side link with no
                    // linked traveler would be an orphan) — traceable
                    // here; the traveler can redo it via the late-link modal.
                    console.warn(
                        "[pa] loyalty decision discarded due to connect failure",
                    );
                }
                setStatus("error");
                setMessage(
                    "We couldn't connect your Movmo account. Please try again.",
                );
            });
    }, [navigate]);

    return (
        <div
            className="min-h-screen flex items-center justify-center"
            style={{ backgroundColor: "#0047ba" }}
        >
            <div
                style={{
                    maxWidth: 420,
                    padding: 32,
                    textAlign: "center",
                    backgroundColor: "#fff",
                    borderRadius: 12,
                }}
            >
                {status === "pending" && (
                    <div
                        className="mx-auto mb-4 animate-spin rounded-full"
                        style={{
                            width: 32,
                            height: 32,
                            border: "3px solid #e6edf8",
                            borderTopColor: "#0047ba",
                        }}
                    />
                )}
                <p style={{ color: "#01296a", fontSize: 16 }}>{message}</p>
                {status === "error" && (
                    <button
                        onClick={() =>
                            navigate("/philippine-airlines", {
                                replace: true,
                            })
                        }
                        style={{
                            marginTop: 20,
                            padding: "10px 20px",
                            backgroundColor: "#0047ba",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            cursor: "pointer",
                            fontWeight: 600,
                        }}
                    >
                        Back to Philippine Airlines
                    </button>
                )}
            </div>
        </div>
    );
}
